import {GetProductApiResponse} from "../../../../react-query-hooks/products/types";
import {EditProductBasicFormStepFormValues} from "../schema-validation";
import {readDigitalProductPriceType} from "../../product-pricing/read-price-type";
import {toPurchaseLimitFormValues} from "../../../../components/offering-form/general/settings/purchase-limit-payload";

/**
 * The saved product in the edit page's form encoding.
 *
 * The update is a full-replace PUT: a field left out of it is reset, not kept. So a tab
 * that edits one part of the product, such as the checkout questions, still has to send
 * everything else exactly as saved. The form tab uses this as its starting values, and
 * the other tabs submit it with their own changes on top, so the two cannot drift.
 *
 * Cover images are left out: the form tab loads them separately.
 */
export function toSavedEditProductFormValues(
  productData: GetProductApiResponse,
  isPwywEnabled: boolean
): Omit<EditProductBasicFormStepFormValues, "cover_images"> {
  const {product, currencies} = productData;
  const savedRedirectType =
    product.redirectType ?? (product.url_redirect ? "url" : "none");

  return {
    coverImage: product.coverImage,
    currencies:
      currencies.length < 1
        ? []
        : currencies
            .filter((c) => !c.is_auto_converted)
            .sort((a, b) => {
              // Put base currency first
              if (a.is_base) return -1;
              if (b.is_base) return 1;
              return 0;
            })
            .map((c) => ({
              callPrice: c.amount,
              callPriceCurrency: c.currency,
              minCallPrice: c.min_call_price,
            })),
    description: product.description,
    hideFromProfile: product.hideFromProfile,
    isUrlRedirect: !!product.url_redirect,
    redirectAfterPurchase: savedRedirectType === "url" ? "custom-url" : savedRedirectType,
    telegramGroupId: product.telegramGroupChatId ?? undefined,
    name: product.title,
    priceType: readDigitalProductPriceType(productData, isPwywEnabled),
    requestCustomerPhoneNumber: product.request_phone_number,
    ...toPurchaseLimitFormValues(product.purchaseLimit),
    url_redirect: product.url_redirect,
    enable_currency_conversion: true,
    selected_currencies:
      product.selected_currencies?.length && product.selected_currencies.length > 0
        ? product.selected_currencies
        : currencies
            .filter((c) => c.is_auto_converted)
            .map((c) => c.currency.toUpperCase()),
  };
}
